import { downloadFile } from "helpers/utility/downloadFile";
import { useCallback, useRef } from "react";
import { Id, toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import { fetchExportStatusApiV1DatasetDatasetIdExportStatusGet } from "vl-api/vlComponents";
import type * as Schemas from "vl-api/vlSchemas";
import { extractFileNameFromUrl } from "../helpers/utility/extractFileNameFromUrl";

const showToast = (node: React.ReactNode): Id => {
  return toast(node, {
    autoClose: false,
    closeButton: false,
    closeOnClick: false,
  });
};

export const useExportPolling = () => {
  const toastIdRef = useRef<Id | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ----------------- Toast Handlers -----------------
  const pendingNotification = useCallback((progress = 0) => {
    toastIdRef.current = showToast(
      <VLToastComponent
        status="IN_PROGRESS"
        progress={progress}
        title="Exporting File..."
        onClose={() => toast.dismiss(toastIdRef.current!)}
        helperText="This process will continue in the background. You can close this window"
        description="Your export is almost ready! You can find your ZIP file in the 'Downloads' folder shortly."
      />,
    );
  }, []);

  const successNotification = useCallback((uri: string) => {
    toast.dismiss(toastIdRef.current!);

    toastIdRef.current = showToast(
      <VLToastComponent
        status="SUCCESS"
        title="The file is now ready!"
        onClose={() => toast.dismiss(toastIdRef.current!)}
        helperText={
          <span>
            You can download the file again by clicking this{" "}
            <a
              href={uri}
              download={extractFileNameFromUrl(uri)}
              rel="noopener noreferrer"
            >
              link
            </a>
            .
          </span>
        }
        description={
          "Your export is almost ready! You can find your ZIP file in the 'Downloads' folder shortly."
        }
      />,
    );
  }, []);

  const errorNotification = useCallback((msg: string) => {
    toast.dismiss(toastIdRef.current!);

    toastIdRef.current = showToast(
      <VLToastComponent
        status="ERROR"
        title="Oops! We could not process your file"
        description={msg}
        onClose={() => toast.dismiss(toastIdRef.current!)}
      />,
    );
  }, []);

  // ----------------- Polling Logic -----------------
  const startPolling = useCallback(
    async (exportTaskId: string, datasetId: string) => {
      // Stop any existing polling
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      pendingNotification(0);

      const poll = async () => {
        try {
          const response =
            await fetchExportStatusApiV1DatasetDatasetIdExportStatusGet({
              pathParams: { datasetId },
              queryParams: { export_task_id: exportTaskId },
            });

          // The API response might be wrapped, so we check for data property
          const status: Schemas.ExportTask =
            (response as any)?.data || response;

          toast.update(toastIdRef.current!, {
            render: (
              <VLToastComponent
                status="IN_PROGRESS"
                progress={status.progress || 0}
                title="Exporting File..."
                onClose={() => toast.dismiss(toastIdRef.current!)}
                helperText="This process will continue in the background. You can close this window"
                description="Your export is almost ready! You can find your JSON file in the 'Downloads' folder."
              />
            ),
          });

          // ---- Stop Polling on Completion or Failure ----
          if (status.status === "COMPLETED") {
            if (status.download_uri) {
              successNotification(status.download_uri);
              downloadFile(extractFileNameFromUrl(status.download_uri), {
                type: "href",
                href: status.download_uri,
              });
              return; // <-- stop polling
            } else {
              errorNotification(
                "Export completed but no download URI available.",
              );
              return; // <-- stop polling
            }
          } else if (
            status.status === "FAILED" ||
            status.status === "REJECTED"
          ) {
            errorNotification(
              status.result_message ||
                "Export failed. Please try again or contact support.",
            );
            return; // <-- stop polling
          }

          pollingIntervalRef.current = setTimeout(poll, 2000);
        } catch (e: any) {
          errorNotification(e?.message || "Failed to check status.");
        }
      };

      poll(); // immediate first call
    },
    [pendingNotification, successNotification, errorNotification],
  );

  return {
    startPolling,
    errorNotification,
  };
};
